import type { IconProps } from '@carboncopyco/icons';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import React from 'react';
import { Spacer } from '../Spacer';
import { TextButton } from '../Typography';

export interface ButtonDropdownProps {
  label: string;
  rightIcon?: React.ReactElement<IconProps>;
  leftIcon?: React.ReactElement<IconProps>;
  onClick?: () => void;
  disabled?: boolean;
  isSelected?: boolean;
  primary?: boolean;
  accent1?: boolean;
  secondary?: boolean;
  natural?: boolean;
  customLabel?: string;
}

export const ButtonDropdown = ({
  label,
  rightIcon,
  leftIcon,
  onClick,
  disabled,
  isSelected = false,
  primary,
  accent1,
  secondary,
  natural,
  customLabel,
}: ButtonDropdownProps) => {
  const [active, setActive] = useBoolean();

  return (
    <div
      className={classNames(
        'flex items-center justify-start py-1.5 px-3 rounded-md cursor-pointer max-w-max',
        {
          'text-ds-natural-200 bg-ds-natural-200-a cursor-default pointer-events-none':
            disabled,
          'bg-ds-natural-50 hover:bg-ds-natural-100': !isSelected || !active,
          'bg-ds-natural-100': isSelected || active,

          'text-ds-primary-300 hover:text-ds-primary-400 active:text-ds-primary-500':
            primary && (!isSelected || !active),
          'text-ds-secondary-300 hover:text-ds-secondary-400 active:text-ds-secondary-500':
            secondary && (!isSelected || !active),
          'text-ds-accent1-300 hover:text-ds-accent1-400 active:text-ds-accent1-500':
            accent1 && (!isSelected || !active),
          'text-ds-natural-300 hover:text-ds-natural-400 active:text-ds-natural-500':
            natural && (!isSelected || !active),
          'text-ds-primary-500': primary && (isSelected || active),
          'text-ds-accent1-500': accent1 && (isSelected || active),
          'text-ds-secondary-500': secondary && (isSelected || active),
          'text-ds-natural-500': natural && (isSelected || active),
          'text-ds-natural-700': isSelected || active,
        }
      )}
      onMouseDown={setActive.setTrue}
      onMouseUp={setActive.setFalse}
      onClick={() => {
        if (!onClick || disabled) {
          return;
        }
        onClick();
      }}
    >
      {leftIcon && React.cloneElement(leftIcon, { size: 20 })}
      <Spacer amount={0.75} />
      {customLabel && (
        <>
          <TextButton
            label={customLabel}
            color="text-current"
          />
          <Spacer amount={0.5} />
        </>
      )}
      <TextButton
        label={label}
        color="text-current"
      />
      <Spacer amount={0.75} />
      {rightIcon && React.cloneElement(rightIcon, { size: 20 })}
    </div>
  );
};
